import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { getAPIBaseURL } from '../services/helpers'
import { getData } from '../services/request'

const StatCard = ({ label, value, icon: Icon }) => (
  <div className="border rounded-lg bg-white px-4 py-3">
    <div className="flex items-center gap-2 text-xs text-slate-500">
      {Icon && <Icon className="h-4 w-4 shrink-0 text-slate-400" />}
      <span className="whitespace-nowrap">{label}</span>
    </div>
    <div className="mt-1 text-xl font-semibold text-slate-900">{value}</div>
  </div>
)

const JobsIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7h16M4 11h16M4 15h10" />
  </svg>
)

const UsersIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20a4 4 0 00-8 0M12 12a4 4 0 100-8 4 4 0 000 8z" />
  </svg>
)

const WalletIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7h18v10H3zM16 12h4" />
  </svg>
)

const InboxIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7h18v10H3zM3 13l4 4h10l4-4" />
  </svg>
)

const EmploymeAdminDashboard = () => {
  const [summary, setSummary] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    const fetchSummary = async () => {
      setLoading(true)
      const url = `${getAPIBaseURL()}/employme/admin/dashboard`
      const { error: reqError, result } = await getData(url, {}, {})
      setLoading(false)
      if (reqError || !result) {
        setError('Unable to load Employ Me admin summary')
        return
      }
      setSummary(result.data.data)
    }
    fetchSummary()
  }, [])

  const safeNumber = (val) => {
    if (val === null || val === undefined) return '—'
    if (typeof val === 'number') return val
    const num = Number(val)
    return Number.isNaN(num) ? val : num
  }

  const jobsTotal = safeNumber(summary?.jobsTotal)
  const employersTotal = safeNumber(summary?.employersTotal)
  const jobSeekersTotal = safeNumber(summary?.jobSeekersTotal)
  const pendingJobs = safeNumber(summary?.pendingJobs)
  const liveJobs = safeNumber(summary?.liveJobs)
  const recentPayments = safeNumber(summary?.recentPaymentsTotal)
  const interviewsTotal = safeNumber(summary?.interviewsTotal)

  return (
    <div className="p-4 space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-slate-900 mb-1">Employ Me overview</h1>
        <p className="text-xs text-slate-500">
          Monitor employers, job seekers, jobs and approvals for the Employ Me product.
        </p>
      </div>
      {error && <div className="mb-3 text-sm text-red-600">{error}</div>}
      {loading && <div className="text-sm text-slate-600">Loading...</div>}

      {summary && (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7">
            <StatCard label="Total jobs" value={jobsTotal} icon={JobsIcon} />
            <StatCard label="Live jobs" value={liveJobs} icon={JobsIcon} />
            <StatCard label="Jobs pending approval" value={pendingJobs} icon={JobsIcon} />
            <StatCard label="Employers" value={employersTotal} icon={UsersIcon} />
            <StatCard label="Job seekers" value={jobSeekersTotal} icon={UsersIcon} />
            <StatCard label="Interviews" value={interviewsTotal} icon={InboxIcon} />
            <StatCard label="Recent payments (7d)" value={recentPayments} icon={WalletIcon} />
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mt-4">
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="flex items-center gap-2 mb-1">
                <InboxIcon className="h-4 w-4 text-slate-400" />
                <h2 className="text-sm font-semibold text-slate-900">Job approvals queue</h2>
              </div>
              <p className="text-xs text-slate-500 mb-3">
                Jobs that have been paid for and are waiting for admin approval before going live.
              </p>
              <p className="text-2xl font-bold text-slate-900">{pendingJobs}</p>
              <Link
                to="/admin/app/employme/job-approvals"
                className="mt-2 inline-block text-xs font-medium text-blue-600 hover:text-blue-800"
              >
                Review and approve →
              </Link>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="flex items-center gap-2 mb-1">
                <JobsIcon className="h-4 w-4 text-slate-400" />
                <h2 className="text-sm font-semibold text-slate-900">Job posting settings</h2>
              </div>
              <p className="text-xs text-slate-500 mb-3">
                Enable or disable payment required before employers can publish job listings.
              </p>
              <Link
                to="/admin/app/employme/settings"
                className="mt-2 inline-block text-xs font-medium text-blue-600 hover:text-blue-800"
              >
                Configure settings →
              </Link>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="flex items-center gap-2 mb-1">
                <JobsIcon className="h-4 w-4 text-slate-400" />
                <h2 className="text-sm font-semibold text-slate-900">Reminder funnels</h2>
              </div>
              <p className="text-xs text-slate-500 mb-3">
                Keep employers moving by reminding them to complete drafts, pay for listings and publish jobs.
              </p>
              <ul className="space-y-1.5 text-xs text-slate-600">
                <li>• Employers registered but have not posted a job</li>
                <li>• Draft jobs created but not paid</li>
                <li>• Approved jobs that are not yet live</li>
              </ul>
              <Link
                to="/admin/app/employme/reminders"
                className="mt-2 inline-block text-xs font-medium text-blue-600 hover:text-blue-800"
              >
                Configure reminders →
              </Link>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-4 md:col-span-2 lg:col-span-1">
              <div className="flex items-center gap-2 mb-1">
                <InboxIcon className="h-4 w-4 text-slate-400" />
                <h2 className="text-sm font-semibold text-slate-900">Templates & messaging</h2>
              </div>
              <p className="text-xs text-slate-500 mb-3">
                Manage the accept / reject / invite templates sent to candidates and system reminders for employers.
              </p>
              <ul className="space-y-1.5 text-xs text-slate-600">
                <li>• Employer → candidate messages</li>
                <li>• Profile and interview completion nudges</li>
                <li>• Video expiration notices</li>
              </ul>
              <Link
                to="/admin/app/employme/templates"
                className="mt-2 inline-block text-xs font-medium text-blue-600 hover:text-blue-800"
              >
                Manage templates →
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default EmploymeAdminDashboard

